import { ViewportScroller } from '@angular/common';
import { Component, EventEmitter, Input, Output, signal, OnInit, AfterViewInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbModal, NgbOffcanvas } from '@ng-bootstrap/ng-bootstrap';
import { BehaviorSubject, combineLatest, forkJoin, Observable, switchMap } from 'rxjs';
import { FingerprintCardValidityStatus } from 'src/shared/constants';
import {
  EducatorsClient,
  EducatorHeaderDto,
  DeletePersonAlertCommand,
  EvaluationsClient,
  ProfessionalDevelopmentRequirementState,
  ScreenShareAvailabilityStateDto,
} from 'src/shared/services/api.service';
import { AddAlertModalComponent } from './add-alert-modal/add-alert-modal.component';
import { openConfirmationModal } from 'src/shared/utilities/modal-utilities';
import { ConfirmationModalComponent } from 'src/shared/confirmation-modal/confirmation-modal.component';
import { NotesService } from '../services/notes.service';
import { AuthService } from 'src/auth/services/auth.service';
import { CertPolicies } from 'src/auth/cert-policies';
import { LinkedEducatorContext } from '../linked-educator-icon/linked-educator-icon.component';
import { EducatorEvent, EducatorEventBusService } from 'src/educators/services/educator-event-bus.service';
import { EducatorNotesComponent } from './educator-notes/educator-notes.component';
import { FeatureManagementService } from 'src/shared/services/feature-management.service';
import { ScreenShareService } from 'src/shared/services/screen-share.service';
import { ToastService } from 'src/shared/services/toast.service';

@Component({
  selector: 'cert-educator-header [personId\\$]',
  templateUrl: './educator-header.component.html',
  styleUrls: ['./educator-header.component.scss'],
  standalone: false,
})
export class EducatorHeaderComponent implements OnInit, AfterViewInit {
  constructor(
    private readonly educatorsClient: EducatorsClient,
    private readonly evaluationsClient: EvaluationsClient,
    private readonly viewportScroller: ViewportScroller,
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly modalService: NgbModal,
    private readonly notesService: NotesService,
    private readonly authService: AuthService,
    private readonly offcanvasService: NgbOffcanvas,
    private readonly refreshComponentService: EducatorEventBusService,
    private readonly featureManager: FeatureManagementService,
    private readonly screenShareService: ScreenShareService,
    private readonly toastService: ToastService,
  ) {}

  @Output() refreshHeaderInfo: EventEmitter<void> = new EventEmitter<void>();
  @Input() personId$!: Observable<number>;
  ProfessionalDevelopmentRequirementState = ProfessionalDevelopmentRequirementState;
  private screenShareEnabledState = signal(false);
  canInitiateEducatorScreenShare: boolean = this.authService.hasPolicy(CertPolicies.CanInitiateEducatorScreenShare);
  private screenShareAvailabilityStateValue = signal<ScreenShareAvailabilityStateDto | undefined>(undefined);
  headerInfoRefresh$ = new BehaviorSubject<void>(undefined);
  private personIdState = signal(0);
  dataLoaded = signal(false);
  private educatorDetailState = signal<EducatorHeaderDto | undefined>(undefined);
  educatorDetail$!: Observable<EducatorHeaderDto>;
  fingerprintCardValidityStatus = FingerprintCardValidityStatus;
  fingerprintIsNew = true;
  private educatorAddressExistsState = signal(false);
  canAddAlerts: boolean = this.authService.hasPolicy(CertPolicies.CanAddAlerts);
  canDeleteAlerts: boolean = this.authService.hasPolicy(CertPolicies.CanDeleteAlerts);
  private linkedEducatorContextState = signal<LinkedEducatorContext>({
    isPrimaryDuplicateEducator: false,
    duplicateEducators: [],
  });

  get personId(): number {
    return this.personIdState();
  }

  set personId(value: number) {
    this.personIdState.set(value);
  }

  get linkedEducatorContext(): LinkedEducatorContext {
    return this.linkedEducatorContextState();
  }

  set linkedEducatorContext(value: LinkedEducatorContext) {
    this.linkedEducatorContextState.set(value);
  }

  get screenShareEnabled(): boolean {
    return this.screenShareEnabledState();
  }

  set screenShareEnabled(value: boolean) {
    this.screenShareEnabledState.set(value);
  }

  get screenShareAvailabilityState(): ScreenShareAvailabilityStateDto | undefined {
    return this.screenShareAvailabilityStateValue();
  }

  set screenShareAvailabilityState(value: ScreenShareAvailabilityStateDto | undefined) {
    this.screenShareAvailabilityStateValue.set(value);
  }

  get educatorDetail(): EducatorHeaderDto {
    return this.educatorDetailState()!;
  }

  set educatorDetail(value: EducatorHeaderDto | undefined) {
    this.educatorDetailState.set(value);
  }

  get educatorAddressExists(): boolean {
    return this.educatorAddressExistsState();
  }

  set educatorAddressExists(value: boolean) {
    this.educatorAddressExistsState.set(value);
  }

  get educatorCurrentEmployment() {
    if (this.educatorDetail.currentEmploymentInfo == null) {
      return 'N/A';
    }

    const schoolString = this.educatorDetail.currentEmploymentInfo.school
      ? `(${this.educatorDetail.currentEmploymentInfo.school})`
      : '';

    return `${this.educatorDetail.currentEmploymentInfo.lea} ${schoolString}`;
  }

  ngOnInit() {
    const refresh$ = this.refreshComponentService.listenFor(
      EducatorEvent.Merge,
      EducatorEvent.DemographicsChange,
      EducatorEvent.FingerprintDisassociated,
      EducatorEvent.SpecialProgramChange,
    );
    combineLatest([
      this.personId$,
      this.headerInfoRefresh$,
      refresh$,
      this.featureManager.isFeatureEnabled('screenShare'),
    ])
      .pipe(
        switchMap(([personId, _, __, screenShareEnabled]) => {
          this.personId = personId;
          this.screenShareEnabled = screenShareEnabled;

          return forkJoin([
            this.educatorsClient.getEducatorManagementHeaderInfo(personId),
            this.educatorsClient.getAddressExistsStatus(personId),
          ]);
        }),
      )
      .subscribe(([headerInfo, addressStatus]) => {
        this.educatorDetail = headerInfo;
        this.linkedEducatorContext = {
          isPrimaryDuplicateEducator: headerInfo.isPrimaryDuplicateEducator,
          duplicateEducators: headerInfo.duplicateEducators,
        };
        this.educatorAddressExists = addressStatus;
        this.dataLoaded.set(true);
        if (this.screenShareEnabled) {
          this.refreshScreenShareAvailability();
        }
      });
  }

  getAddressExistsStatus() {
    this.educatorsClient.getAddressExistsStatus(this.personId).subscribe((addressStatus) => {
      this.educatorAddressExists = addressStatus;
    });
  }

  ngAfterViewInit() {
    const fontSize = window?.getComputedStyle(document.body).getPropertyValue('font-size');

    if (fontSize) {
      const headerHeight = Number(fontSize.match(/\d+/)) * 3;
      this.viewportScroller.setOffset([0, headerHeight]);
    }
  }

  openAddAlertModal() {
    const modalRef = this.modalService.open(AddAlertModalComponent, {
      backdrop: 'static',
      size: 'sm',
      centered: true,
    });

    modalRef.componentInstance.personId = this.personId;

    modalRef.result.then(() => {
      this.headerInfoRefresh$.next();
      this.notesService.getNotes(this.personId);
    });
  }

  openDeleteAlertModal(personAlertId: number) {
    openConfirmationModal(
      this.modalService,
      'Delete Alert',
      'Are you sure you would like to delete this alert? This action cannot be undone.',
      () => {
        const command: DeletePersonAlertCommand = {
          personId: this.personId,
          personAlertId: personAlertId,
        };

        this.educatorsClient.deletePersonAlert(command).subscribe({
          next: () => {
            this.headerInfoRefresh$.next();
            this.notesService.getNotes(this.personId);
          },
        });
      },
    );
  }

  badgeClassWhenNotOnFile() {
    return {
      'badge-warning': this.educatorDetail.pendingIVPCardExists,
      'badge-danger': !this.educatorDetail.pendingIVPCardExists,
    };
  }

  navigateToEducatorManagement(event: Event) {
    event.preventDefault();
    this.router.navigateByUrl(`/cert/educators/${this.personId}`);
  }

  openEducatorNotesCanvas() {
    const notes = this.offcanvasService.open(EducatorNotesComponent, {
      position: 'end',
      scroll: true,
      ariaLabelledBy: 'educatorNotesOffCanvasLabel',
    });
    notes.componentInstance.personId = this.personId;
  }

  refreshScreenShareAvailability() {
    this.evaluationsClient.getScreenShareAvailabilityState(this.personId).subscribe((state) => {
      this.screenShareAvailabilityState = state;
    });
  }

  requestScreenShare() {
    if (this.screenShareEnabled && this.screenShareAvailabilityState?.targetADEConnectUserAccount) {
      this.screenShareService.requestScreenShare(this.screenShareAvailabilityState.targetADEConnectUserAccount);
      this.toastService.success(
        'A request to start a screen share session has been sent to the educator. If they accept, you will be able to view their screen and assist them in real time.',
      );
    }
  }
}
